import { ChangeEvent, CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Webcam from 'react-webcam';
import { determineDeviceType } from 'src/AppParameters';
import { DeviceType } from 'src/DeviceType';
import { EasyClaimDocumentType, VehicleType } from 'src/easyclaim.api.client.typescript/src';
import {
	FrontLeft,
	FrontRight,
	RearLeft,
	RearRight,
	Vin,
} from 'src/easyclaim.app.assets/artwork/overlays';
import { RotatePhone } from 'src/easyclaim.app.assets/icons/RotatePhone';
import { CustomerConfiguration } from 'src/easyclaim.app.contexts/CustomerConfigurationContextCore';
import { reportError } from 'src/easyclaim.app.libs/reportError';

import { CameraCaptureNavigation, CameraOrientation } from './CameraCaptureNavigation';
import { type DeviceInfo, getCurrentPosition } from './cameraExifUtils';
import styles from './styles/CameraWithOverlay.module.css';

function getOrientation(): CameraOrientation {
	if (window.innerWidth <= window.innerHeight) return 'portrait';
	const angle =
		screen.orientation?.angle ?? (window as Window & { orientation?: number }).orientation ?? 0;

	return angle === 270 || angle === -90 ? 'landscapeRight' : 'landscapeLeft';
}

interface CameraWithOverlayProps {
	isOpen: boolean;
	overlayImage?: string;
	vehicleType?: VehicleType;
	fileType?: EasyClaimDocumentType;
	onCapture: (imageSrc: string, trackInfo?: Partial<DeviceInfo>) => void;
	onClose: () => void;
}

const supportsGetUserMedia = () => {
	return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

interface ExtendedMediaTrackSettings extends MediaTrackSettings {
	exposureTime?: number;
	iso?: number;
	focalLength?: number;
	aperture?: number;
	focusDistance?: number;
}

interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
	focusDistance?: { min: number; max: number };
}

export const CameraWithOverlay: FC<CameraWithOverlayProps> = ({
	isOpen,
	vehicleType,
	fileType,
	onCapture,
	onClose,
}) => {
	const webcamRef = useRef<Webcam>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isCameraReady, setIsCameraReady] = useState(false);
	const [shouldUseNativeCamera, setShouldUseNativeCamera] = useState(false);
	const [capturedImage, setCapturedImage] = useState<string | null>(null);
	const [isOverlayVisible, setIsOverlayVisible] = useState(true);
	const [isProcessing, setIsProcessing] = useState(false);
	const [orientation, setOrientation] = useState<CameraOrientation>('portrait');
	const [wasLandscapeAlertShown, setWasLandscapeAlertShown] = useState(false);
	const [isLandscapeModalVisible, setIsLandscapeModalVisible] = useState(false);
	const [isDark, setIsDark] = useState(false);
	const videoTrackRef = useRef<MediaStreamTrack | null>(null);
	const darkCheckCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const darkCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const customerConfiguration = CustomerConfiguration();
	const isDesktop = determineDeviceType() === DeviceType.desktop;
	const isOverlayEnabled = !isDesktop;
	const navigationOrientation = isDesktop ? 'portrait' : orientation;

	const getOverlayImage = () => {
		switch (vehicleType) {
			case VehicleType.Car:
				switch (fileType) {
					case EasyClaimDocumentType.FrontLeftPicture:
						return <FrontLeft />;
					case EasyClaimDocumentType.FrontRightPicture:
						return <FrontRight />;
					case EasyClaimDocumentType.RearLeftPicture:
						return <RearLeft />;
					case EasyClaimDocumentType.RearRightPicture:
						return <RearRight />;
					case EasyClaimDocumentType.VinPicture:
						return <Vin />;
					default:
						return undefined;
				}
			default:
				return undefined;
		}
	};

	useEffect(() => {
		if (isOpen) {
			void getCurrentPosition();
		}

		if (isOpen && !supportsGetUserMedia()) {
			setShouldUseNativeCamera(true);
		}

		return () => {
			setIsCameraReady(false);
			videoTrackRef.current = null;
			setWasLandscapeAlertShown(false);
			setCapturedImage(null);
			setShouldUseNativeCamera(false);
			setIsProcessing(false);
			setIsDark(false);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const checkOrientation = () => {
			setOrientation(getOrientation());
		};

		checkOrientation();

		window.addEventListener('resize', checkOrientation);
		const mql = window.matchMedia('(orientation: landscape)');
		const handleChange = () => checkOrientation();

		mql.addEventListener('change', handleChange);

		return () => {
			window.removeEventListener('resize', checkOrientation);
			mql.removeEventListener('change', handleChange);
		};
	}, [isOpen]);

	useEffect(() => {
		const isFullscreen = !!(
			document.fullscreenElement ||
			(document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement
		);

		if (
			isOpen &&
			orientation !== 'portrait' &&
			!wasLandscapeAlertShown &&
			!shouldUseNativeCamera &&
			!isFullscreen
		) {
			setWasLandscapeAlertShown(true);
			setIsLandscapeModalVisible(true);
		}
	}, [isOpen, orientation, wasLandscapeAlertShown, shouldUseNativeCamera]);

	useEffect(() => {
		if (customerConfiguration?.canDetectDarkPictureLive) {
			if (!isCameraReady || !isOpen) {
				setIsDark(false);

				return;
			}

			if (!darkCheckCanvasRef.current) {
				darkCheckCanvasRef.current = document.createElement('canvas');
				darkCheckCanvasRef.current.width = 64;
				darkCheckCanvasRef.current.height = 36;
			}

			const schedule = () => {
				darkCheckTimerRef.current = setTimeout(() => {
					const video = webcamRef.current?.video;
					const canvas = darkCheckCanvasRef.current;

					if (video && canvas && video.readyState >= 2) {
						const ctx = canvas.getContext('2d');

						if (ctx) {
							ctx.drawImage(video, 0, 0, 64, 36);
							const { data } = ctx.getImageData(0, 0, 64, 36);
							let sum = 0;

							for (let i = 0; i < data.length; i += 4) {
								sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
							}
							const avgLuminance = sum / (64 * 36);

							setIsDark(avgLuminance < 50);
						}
					}
					schedule();
				}, 2000);
			};

			schedule();

			return () => {
				if (darkCheckTimerRef.current) clearTimeout(darkCheckTimerRef.current);
			};
		}
	}, [isCameraReady, isOpen, customerConfiguration?.canDetectDarkPictureLive]);

	useEffect(() => {
		if (isOpen && shouldUseNativeCamera && fileInputRef.current && !capturedImage) {
			const timeout = setTimeout(() => {
				fileInputRef.current?.click();
			}, 100);

			return () => clearTimeout(timeout);
		}
	}, [isOpen, shouldUseNativeCamera, capturedImage]);

	useEffect(() => {
		const input = fileInputRef.current;

		if (!input || !isOpen || !shouldUseNativeCamera) return;

		const handleCancel = () => {
			if (!capturedImage) {
				onClose();
			}
		};

		input.addEventListener('cancel', handleCancel);

		return () => {
			input.removeEventListener('cancel', handleCancel);
		};
	}, [isOpen, shouldUseNativeCamera, capturedImage, onClose]);

	const capture = useCallback(() => {
		if (isProcessing) return;
		setIsProcessing(true);
		try {
			const imageSrc = webcamRef.current?.getScreenshot();

			if (imageSrc) {
				const videoTrack =
					webcamRef.current?.video?.srcObject instanceof MediaStream
						? webcamRef.current.video.srcObject.getVideoTracks()[0]
						: null;

				const trackInfo: Partial<DeviceInfo> = {};

				if (videoTrack) {
					try {
						const settings = videoTrack.getSettings() as ExtendedMediaTrackSettings;

						if (settings.exposureTime !== undefined) {
							if (settings.exposureTime > 1000) {
								trackInfo.exposureTime = settings.exposureTime / 1_000_000;
							} else if (settings.exposureTime > 1) {
								trackInfo.exposureTime = settings.exposureTime / 1000;
							} else {
								trackInfo.exposureTime = settings.exposureTime;
							}
						}
						if (settings.iso !== undefined) {
							trackInfo.isoSpeed = settings.iso;
						}
						if (settings.focalLength !== undefined) {
							trackInfo.focalLength = settings.focalLength;
						}
						if (settings.aperture !== undefined) {
							trackInfo.fStop = settings.aperture;
						}
						if (settings.focusDistance !== undefined) {
							const capabilities = videoTrack.getCapabilities() as ExtendedMediaTrackCapabilities;
							const min = capabilities?.focusDistance?.min;
							const max = capabilities?.focusDistance?.max;

							if (min !== undefined && max !== undefined && max > min) {
								const range = max - min;
								const isNotDefault =
									Math.abs(settings.focusDistance - min) > range * 0.01 &&
									Math.abs(settings.focusDistance - max) > range * 0.01;

								if (isNotDefault) {
									trackInfo.subjectDistance = settings.focusDistance;
								}
							}
						}
						trackInfo.flashMode = 0;
					} catch (e) {
						reportError(`Could not read track settings before close: ${e}`, {
							context: 'CameraWithOverlay.capture',
						});
					}
				}

				onClose();
				onCapture(imageSrc, trackInfo);
			}
		} catch (e) {
			reportError(`Screenshot error: ${e}`, { context: 'CameraWithOverlay.capture' });
		} finally {
			setIsProcessing(false);
		}
	}, [onCapture, onClose, isProcessing]);

	const handleNativeCapture = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];

			if (!file) {
				onClose();

				return;
			}

			const reader = new FileReader();

			reader.onload = () => {
				const dataUrl = reader.result as string;

				setCapturedImage(dataUrl);
			};
			reader.readAsDataURL(file);
		},
		[onClose]
	);

	const handleDeny = useCallback(() => {
		setCapturedImage(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
		onClose();
	}, [onClose]);

	const toggleOverlay = useCallback(() => {
		setIsOverlayVisible((prev) => !prev);
	}, []);

	const flexDirection =
		navigationOrientation === 'landscapeLeft'
			? 'row'
			: navigationOrientation === 'landscapeRight'
				? 'row-reverse'
				: 'column';

	if (!isOpen) return null;

	if (shouldUseNativeCamera) {
		const isMobile = determineDeviceType() === DeviceType.mobile;
		const previewNavigation = capturedImage && (
			<CameraCaptureNavigation
				mode="preview"
				orientation={navigationOrientation}
				onDeny={handleDeny}
				onAccept={() => onCapture(capturedImage)}
			/>
		);

		return (
			<div
				className={styles.fullScreenContainer}
				style={{ '--flex-direction': flexDirection } as CSSProperties}
			>
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					className={styles.hiddenInput}
					{...(isMobile ? { capture: 'environment' } : {})}
					onChange={handleNativeCapture}
				/>
				{capturedImage && isDesktop ? (
					<div className={styles.desktopPreview}>
						<img className={styles.previewImage} src={capturedImage} alt="captured" />
						<div className={styles.previewNavigation}>{previewNavigation}</div>
					</div>
				) : capturedImage ? (
					<>
						<div className={styles.landscapeWrapper}>
							<img className={styles.previewImage} src={capturedImage} alt="captured" />
							{isOverlayEnabled && isOverlayVisible && (
								<div className={styles.overlayContainer}>{getOverlayImage()}</div>
							)}
						</div>
						<div className={styles.spacer} />
						{previewNavigation}
					</>
				) : null}
			</div>
		);
	}

	return (
		<>
			<div
				className={styles.fullScreenContainer}
				style={{ '--flex-direction': flexDirection } as CSSProperties}
			>
				<Webcam
					ref={webcamRef}
					audio={false}
					screenshotFormat="image/jpeg"
					screenshotQuality={1}
					forceScreenshotSourceSize={true}
					videoConstraints={
						{
							facingMode: { exact: 'environment' },
							width: { ideal: 4096 },
							height: { ideal: 2160 },
							aspectRatio: { ideal: 16 / 9 },
							resizeMode: 'none',
						} as MediaTrackConstraints
					}
					onUserMedia={(stream: MediaStream) => {
						videoTrackRef.current = stream.getVideoTracks()[0] ?? null;
						setIsCameraReady(true);
					}}
					onUserMediaError={() => {
						setShouldUseNativeCamera(true);
					}}
					style={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						zIndex: 1,
					}}
				/>
				<div className={styles.overlayArea}>
					{isCameraReady && isOverlayEnabled && isOverlayVisible && (
						<div className={styles.overlayContainer}>{getOverlayImage()}</div>
					)}
					{isCameraReady && isDark && (
						<div className={styles.darkWarningBanner} data-orientation={orientation}>
							<FormattedMessage id="camera.darkWarning" />
						</div>
					)}
				</div>
				{isCameraReady && (
					<CameraCaptureNavigation
						mode="live"
						orientation={navigationOrientation}
						onClose={onClose}
						onCapture={capture}
						isCaptureDisabled={!isCameraReady || isProcessing || orientation !== 'portrait'}
						hasOverlayToggle={isOverlayEnabled && getOverlayImage() !== undefined}
						isOverlayVisible={isOverlayVisible}
						onToggleOverlay={toggleOverlay}
					/>
				)}
				{isLandscapeModalVisible && (
					<div className={styles.landscapeModalOverlay}>
						<div className={styles.landscapeModalBox}>
							<RotatePhone />
							<h3 className={styles.landscapeModalTitle}>
								<FormattedMessage id="landscapeModeDetected.title" />
							</h3>
							<p className={styles.landscapeModalText}>
								<FormattedMessage id="landscapeModeDetected.text" />
							</p>
							<button
								className={styles.landscapeModalButton}
								onClick={() => setIsLandscapeModalVisible(false)}
							>
								OK
							</button>
						</div>
					</div>
				)}
			</div>
		</>
	);
};
